import { useContext, useRef, useState } from 'react'
import { Check, Ban, Plus, Minus } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Generic Elasticsearch-style facet list bound to a store path.
 *
 * Renders a list of aggregation buckets (`[{key, doc_count}]`) as a
 * single- or multi-select control and writes the selection back to the
 * store. It deliberately owns the selection toggle in JS — array membership
 * for multi-select, scalar set for single — which side-steps reactive-json's
 * documented "toggle race" (two conditional `setData` on one event see each
 * other) and the lack of an array-membership primitive in YAML.
 *
 * The refetch is NOT done here : wrap the YAML element with the usual
 * `actions:` (reset pagination + `submitData`). Because the whole control is
 * the immediate child of `<ActionDependant>`, a click on any bucket bubbles
 * up to the action handler — the bucket's own handler (which writes the
 * store) runs first, so `submitData` posts the fresh selection.
 *
 * YAML usage:
 *   - type: FacetList
 *     buckets: ~~.esResult.aggregations.by_brand.buckets
 *     dataLocation: ~~.selectedFilters.by_brand   # array (multi) or scalar (single)
 *     mode: checkbox | chip | radio | swatch | tristate  # default checkbox
 *     showCount: true
 *     countKey: contents.value                    # sous-agg à afficher au lieu
 *                                                 # de doc_count (cardinality…)
 *     searchable: true                            # local text filter of buckets
 *     allLabel: 'Toutes'                           # radio only: prepend an "all" (value '')
 *     labelMap: { academie_article: 'Article', ... } # display label per bucket key
 *                                                  # (the raw key stays the filter value)
 *     colorMap: { Blue: 'bg-blue-500', ... }       # swatch only
 *     excludeKeys: ['']                            # buckets à retirer (cf. plus bas)
 *     listClass: 'space-y-0.5'                     # enveloppe de la liste ; défaut
 *                                                  # 'max-h-60 overflow-y-auto space-y-0.5'
 *     actions:
 *       - { what: setData, on: click, path: ~~.pagination.from, value: 0 }
 *       - { what: submitData, on: click, requestKey: esResult-refresh, url: ~~.esQueryUrl,
 *           httpMethod: POST, data: { filter: ~~.selectedFilters, pagination: ~~.pagination },
 *           updateOnlyData: true, updateDataAtLocation: ~~.esResult, refreshAppOnResponse: true }
 *
 * ── Chip mode (rangée horizontale) ────────────────────────────────────────
 * `mode: chip` rend la MÊME sélection (tableau en multi, scalaire en simple) en
 * pastilles qui s'enchaînent horizontalement — recettes DS `tag` / `tag-iris` —
 * au lieu d'une liste verticale de cases à cocher. C'est la forme d'une barre de
 * filtres posée en haut d'un écran (tableau de bord), là où `checkbox` est celle
 * d'une colonne de facettes. Aucune logique de sélection n'est dupliquée : seul
 * le rendu d'un bucket change. L'état sélectionné n'est PAS porté par la seule
 * couleur (coche + `aria-pressed`).
 *
 * ── Tri-state mode (include / exclude per value) ──────────────────────────
 * `mode: tristate` gives each bucket TWO explicit, separate actions : a "+"
 * button (include) and a "−" button (exclude). Each is its own toggle —
 * re-clicking the active action returns the value to neutral. The bound store
 * slot is the BACKEND filter shape itself — `{ any:[], none:[] }` — so no
 * POST-time translation is needed (the Elasticsearch endpoint reads `any`=OR
 * (include) / `none`=NOT (exclude) directly, dropping empty lists). There is no
 * "match all" (ET) mode any more : a multi-valued facet (tags) simply OR-s its
 * includes like every other field. A legacy `all` bucket left in stored state is
 * tolerated on read (folded into the includes) but never written.
 *
 *   - type: FacetList
 *     mode: tristate
 *     buckets: ~~.esResult.aggregations.by_task_state.buckets   # résultat FILTRÉ :
 *                                                  # la facette suit les autres filtres
 *     dataLocation: ~~.selectedFilters.task_state   # { any:[], none:[] }
 */

// ── Pure tri-state helpers (exported for unit tests) ────────────────────────
// The tri-state model is the BACKEND shape `{ any:[], none:[] }` — `any` = OU
// (include, OR within the field), `none` = NOT (exclude). The legacy `all` (ET)
// bucket is GONE : the user found the OU/ET distinction incomprehensible, so the
// UI now offers two explicit per-value actions (+ include / − exclude) and no
// "match all" mode. The helpers still TOLERATE a stray `all` on read (older
// stored state) by folding it into the includes, but never WRITE one.
//
// These pure functions freeze the model semantics so a future reactive-json
// change (e.g. `setData` batching) cannot silently break include/exclude.

const triAsStrList = (v) => (Array.isArray(v) ? v.map(String) : [])

/**
 * Normalise a stored slot into `{ any, none }` of string arrays. A legacy `all`
 * bucket (pre-simplification state) is merged into `any` so old selections keep
 * working ; the model written back never carries `all`.
 */
export const triModelOf = (model) => {
  const m = model && typeof model === 'object' && !Array.isArray(model) ? model : {}
  const any = triAsStrList(m.any)
  const all = triAsStrList(m.all)
  // Fold a legacy `all` into the includes (de-duplicated), drop `all` on write.
  const merged = all.length ? [...any, ...all.filter((v) => !any.includes(v))] : any
  return { any: merged, none: triAsStrList(m.none) }
}

/** Tri-state of a value : 'excluded' (none) | 'included' (any) | 'neutral'. */
export const triStateOf = (model, key) => {
  const k = String(key)
  const m = triModelOf(model)
  if (m.none.includes(k)) return 'excluded'
  if (m.any.includes(k)) return 'included'
  return 'neutral'
}

/**
 * Toggle a value's INCLUDE state on the `{ any, none }` model. Clicking the "+"
 * button : if the value is already included → remove it (back to neutral) ; else
 * include it (and drop any prior exclusion). A value is NEVER in both buckets.
 */
export const setInclude = (model, key) => {
  const k = String(key)
  const m = triModelOf(model)
  const without = (arr) => arr.filter((v) => v !== k)
  if (m.any.includes(k)) {
    // Already included → clicking "+" again clears it (neutral).
    return { any: without(m.any), none: without(m.none) }
  }
  return { any: [...without(m.any), k], none: without(m.none) }
}

/**
 * Toggle a value's EXCLUDE state on the `{ any, none }` model. Clicking the "−"
 * button : if the value is already excluded → remove it (back to neutral) ; else
 * exclude it (and drop any prior inclusion). A value is NEVER in both buckets.
 */
export const setExclude = (model, key) => {
  const k = String(key)
  const m = triModelOf(model)
  const without = (arr) => arr.filter((v) => v !== k)
  if (m.none.includes(k)) {
    // Already excluded → clicking "−" again clears it (neutral).
    return { any: without(m.any), none: without(m.none) }
  }
  return { any: without(m.any), none: [...without(m.none), k] }
}

export const FacetList = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)
  const [search, setSearch] = useState('')

  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const mode = resolve(props.mode) || 'checkbox'
  const isRadio = mode === 'radio'
  const isSwatch = mode === 'swatch'
  const isChip = mode === 'chip'
  const isTristate = mode === 'tristate'
  const showCount = resolve(props.showCount) ?? true
  // Le nombre affiché par bucket. `doc_count` compte des DOCUMENTS : sur un
  // index qui indexe une entité une fois PAR TRADUCTION (ou toute liste que la
  // requête `collapse`), ce n'est pas le nombre de lignes listées. `countKey`
  // désigne alors une sous-agrégation qui, elle, les compte — typiquement une
  // `cardinality` (`countKey: contents.value`). Défaut inchangé : `doc_count`.
  const countKey = resolve(props.countKey)
  const countOf = (b) =>
    typeof countKey === 'string' && countKey
      ? countKey.split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), b) ?? b.doc_count
      : b.doc_count
  const searchable = resolve(props.searchable) ?? false
  const allLabel = resolve(props.allLabel)
  const colorMap = resolve(props.colorMap) || {}
  const labelMap = resolve(props.labelMap) || {}
  const labelFor = (key) => (labelMap[key] != null ? labelMap[key] : key)

  // Buckets whose key is in `excludeKeys` are dropped. Notably used to remove
  // the empty-string bucket (`''`) from a `radio` facet: `''` doubles as the
  // "no filter" sentinel (toggle-off / allLabel value), so an `''` bucket would
  // appear selected by default and be impossible to clear.
  const excludeKeys = resolve(props.excludeKeys)
  const excluded = Array.isArray(excludeKeys) ? excludeKeys.map(String) : []

  // Enveloppe de la liste de buckets. Par défaut elle plafonne et défile (panneau
  // de dropdown) ; une colonne de facettes qui a la place peut passer sa propre
  // classe pour tout montrer d'un coup.
  const listClass = resolve(props.listClass) || 'max-h-60 overflow-y-auto space-y-0.5'

  let buckets = resolve(props.buckets)
  if (!Array.isArray(buckets)) buckets = []
  if (excluded.length) buckets = buckets.filter((b) => !excluded.includes(String(b.key)))

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: isTristate ? {} : isRadio ? '' : [],
    globalDataContext,
    templateContext,
  })

  // A checked value stays listed even when the buckets no longer carry it. With
  // live facets (`buckets` read from the filtered result), ANOTHER facet's
  // selection can empty this value's bucket — dropping it would leave a filter
  // active that the user can no longer see nor uncheck. It comes back with a
  // count of 0, which is exactly what the other filters leave of it.
  const tri = isTristate ? triModelOf(formData) : null
  const selectedKeys = isTristate
    ? [...tri.any, ...tri.none]
    : isRadio
      ? (formData != null && formData !== '' ? [formData] : [])
      : (Array.isArray(formData) ? formData : [])
  const listedKeys = new Set(buckets.map((b) => String(b.key)))
  selectedKeys.forEach((k) => {
    const s = String(k)
    if (!listedKeys.has(s) && !excluded.includes(s)) {
      listedKeys.add(s)
      buckets = [...buckets, { key: k, doc_count: 0 }]
    }
  })

  // ── Tri-state model : the stored slot IS the backend `{any,none}` shape.
  // A value is "included" when present in `any`, "excluded" when in `none`. The
  // two explicit per-value actions (include "+" / exclude "−") are the pure
  // `setInclude` / `setExclude` helpers above (exported & unit-tested) ; each is
  // its own toggle (re-clicking the active action returns the value to neutral).
  const triState = (key) => triStateOf(formData, key)
  const includeValue = (key) => globalDataContext.updateData(setInclude(formData, key), formDataPath)
  const excludeValue = (key) => globalDataContext.updateData(setExclude(formData, key), formDataPath)

  const selectedArray = Array.isArray(formData) ? formData : []
  const isSelected = (key) => (isRadio ? formData === key : selectedArray.includes(key))

  const toggle = (key) => {
    if (isRadio) {
      // Click the active option again to clear it.
      globalDataContext.updateData(formData === key ? '' : key, formDataPath)
      return
    }
    const next = selectedArray.includes(key)
      ? selectedArray.filter((v) => v !== key)
      : [...selectedArray, key]
    globalDataContext.updateData(next, formDataPath)
  }

  const filtered = searchable && search
    ? buckets.filter((b) => String(labelFor(b.key)).toLowerCase().includes(search.toLowerCase()))
    : buckets

  const renderBucket = (b) => {
    const key = b.key
    const selected = isSelected(key)
    const count = countOf(b)

    if (isTristate) {
      const state = triState(key) // 'neutral' | 'included' | 'excluded'
      const label = String(labelFor(key))
      const isIncluded = state === 'included'
      const isExcluded = state === 'excluded'
      // Two explicit, separate actions per value : "+" (include) and "−"
      // (exclude). Each is its own toggle (re-clicking the active one clears it).
      // The active state is conveyed by icon + text-in-aria-label, not colour
      // alone (Check ✓ for include, Ban ⃠ for exclude), and each button is a real
      // <button> so the click bubbles to the wrapping ActionDependant and runs
      // the YAML `actions:` (reset + esFetch refetch).
      return (
        <div
          key={key}
          className={`flex items-center gap-2 w-full px-2.5 py-1.5 rounded-xl transition-colors ${
            isIncluded ? 'bg-indigo-50' : isExcluded ? 'bg-rose-50' : 'hover:bg-gray-50'
          }`}
        >
          <button
            type="button"
            data-facet-control
            onClick={() => includeValue(key)}
            aria-pressed={isIncluded}
            title={isIncluded ? `Inclus — cliquer pour annuler` : `Inclure ${label}`}
            className={`text-sm flex-1 truncate text-left ${
              isIncluded ? 'text-indigo-700 font-medium'
                : isExcluded ? 'text-rose-700 line-through'
                  : 'text-gray-700'
            }`}
          >
            {label}
          </button>
          {showCount && <span className="text-xs font-semibold text-gray-400 tabular-nums">{count}</span>}
          <span className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              data-facet-control
              onClick={() => includeValue(key)}
              aria-pressed={isIncluded}
              aria-label={isIncluded ? `Ne plus inclure ${label}` : `Inclure ${label}`}
              title={isIncluded ? `Inclus — cliquer pour annuler` : `Inclure ${label}`}
              className={`flex items-center justify-center w-6 h-6 rounded-lg border transition-colors ${
                isIncluded
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'border-gray-300 text-gray-500 hover:border-indigo-400 hover:text-indigo-600'
              }`}
            >
              {isIncluded ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              data-facet-control
              onClick={() => excludeValue(key)}
              aria-pressed={isExcluded}
              aria-label={isExcluded ? `Ne plus exclure ${label}` : `Exclure ${label}`}
              title={isExcluded ? `Exclu — cliquer pour annuler` : `Exclure ${label}`}
              className={`flex items-center justify-center w-6 h-6 rounded-lg border transition-colors ${
                isExcluded
                  ? 'bg-rose-600 border-rose-600 text-white'
                  : 'border-gray-300 text-gray-500 hover:border-rose-400 hover:text-rose-600'
              }`}
            >
              {isExcluded ? <Ban className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
            </button>
          </span>
        </div>
      )
    }

    if (isChip) {
      // Pastille : la coche (et `aria-pressed`) porte l'état, pas seulement la
      // couleur. Un vrai <button> pour que le clic remonte à l'ActionDependant
      // comme dans les autres modes.
      return (
        <button
          key={key}
          type="button"
          data-facet-control
          onClick={() => toggle(key)}
          aria-pressed={selected}
          className={`tag ${selected ? 'tag-iris font-semibold' : 'hover:bg-gray-200'}`}
        >
          {selected && <Check className="w-3 h-3 shrink-0" />}
          <span className="truncate max-w-48">{labelFor(key)}</span>
          {showCount && <span className="tabular-nums opacity-70">{count}</span>}
        </button>
      )
    }

    if (isSwatch) {
      const dot = colorMap[key] || 'bg-gray-300'
      return (
        <button
          key={key}
          type="button"
          data-facet-control
          title={showCount ? `${labelFor(key)} (${count})` : String(labelFor(key))}
          onClick={() => toggle(key)}
          className={`relative w-7 h-7 rounded-full ${dot} transition-all ${
            selected ? 'ring-2 ring-offset-2 ring-indigo-400 scale-110' : 'opacity-70 hover:opacity-100'
          }`}
        >
          {selected && <Check className="absolute inset-0 m-auto w-3.5 h-3.5 text-white" />}
        </button>
      )
    }

    return (
      <button
        key={key}
        type="button"
        data-facet-control
        onClick={() => toggle(key)}
        className={`flex items-center gap-2.5 w-full text-left px-2.5 py-2 rounded-xl transition-colors ${
          selected ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-700'
        }`}
      >
        <span
          className={`flex items-center justify-center w-4 h-4 shrink-0 border-2 transition-colors ${
            isRadio ? 'rounded-full' : 'rounded'
          } ${selected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}
        >
          {selected && <Check className="w-2.5 h-2.5 text-white" />}
        </span>
        <span className="text-sm flex-1 truncate">{labelFor(key)}</span>
        {showCount && <span className="text-xs font-semibold text-gray-400">{count}</span>}
      </button>
    )
  }

  // The whole list is wrapped in `<ActionDependant>` whose `onClick` (the YAML
  // `actions:` = reset + esFetch refetch) is bound to `containerRef` and fires
  // for ANY click that bubbles up to it. That leaks : clicking dead space (panel
  // background, padding, counts, the search field) would re-run the search even
  // though no selection changed. We gate at the capture phase : only clicks that
  // originate from a real selection control (`data-facet-control`) are allowed
  // to reach the wrapper ; everything else has its propagation stopped here, so
  // the refetch runs on genuine include/exclude/toggle changes only.
  const gateContainerClick = (e) => {
    if (!e.target.closest('[data-facet-control]')) e.stopPropagation()
  }

  return (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <div
        ref={containerRef}
        onClickCapture={gateContainerClick}
        className={isSwatch ? 'flex flex-wrap gap-3 p-3' : isChip ? 'flex flex-wrap items-center gap-1.5' : 'p-1.5'}
      >
        {searchable && (
          <input
            type="text"
            value={search}
            placeholder="Rechercher…"
            autoComplete="off"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full mb-1.5 text-sm px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 outline-none focus:ring-2 focus:ring-indigo-400"
          />
        )}
        {isRadio && allLabel != null && isChip && (
          <button
            type="button"
            data-facet-control
            onClick={() => globalDataContext.updateData('', formDataPath)}
            aria-pressed={!formData}
            className={`tag ${!formData ? 'tag-iris font-semibold' : 'hover:bg-gray-200'}`}
          >
            {!formData && <Check className="w-3 h-3 shrink-0" />}
            <span className="truncate max-w-48">{allLabel}</span>
          </button>
        )}
        {isRadio && allLabel != null && !isChip && (
          <button
            type="button"
            data-facet-control
            onClick={() => globalDataContext.updateData('', formDataPath)}
            className={`flex items-center gap-2.5 w-full text-left px-2.5 py-2 rounded-xl transition-colors ${
              !formData ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-700'
            }`}
          >
            <span
              className={`flex items-center justify-center w-4 h-4 shrink-0 border-2 rounded-full ${
                !formData ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
              }`}
            >
              {!formData && <Check className="w-2.5 h-2.5 text-white" />}
            </span>
            <span className="text-sm flex-1 truncate">{allLabel}</span>
          </button>
        )}
        <div className={isSwatch || isChip ? 'contents' : listClass}>
          {filtered.map(renderBucket)}
        </div>
      </div>
    </ActionDependant>
  )
}
